import { ref } from 'vue';
import ModelRepository from '@/repositories/model/ModelRepository.ts';
import {
    Model,
    ModelCreateForm,
    ModelDeliveryPriceStatus,
    ModelsPaginator,
    ModelsPaginatorItem,
    ModelsRequestFilter,
    ModelUpdateForm,
} from '@/types/model.type.ts';
import { PaginationMeta } from '@/types/pagination-meta.ts';
import useLoading from '@/utils/useLoading.ts';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function useModelRepository() {
    const arModels = ref<ModelsPaginatorItem[]>([]);
    const modelsPaginationMeta = ref<PaginationMeta | null>(null);
    const { isLoading: isModelsLoading, setLoading: setModelsLoading } = useLoading();
    const modelRepository = new ModelRepository();

    const getModels = (requestFilter?: ModelsRequestFilter): void => {
        setModelsLoading(true);

        modelRepository.getModels(requestFilter)
            .then((modelsPaginator: ModelsPaginator) => {
                arModels.value = modelsPaginator.data;
                modelsPaginationMeta.value = modelsPaginator.meta;

                setModelsLoading(false);
            });
    };

    const getModel = (id: number): Promise<Model> => {
        return modelRepository.getModel(id);
    };

    const createModel = (createForm: ModelCreateForm): Promise<void> => {
        return modelRepository.createModel(createForm);
    };

    const updateModel = (id: number, updateForm: ModelUpdateForm): Promise<void> => {
        return modelRepository.updateModel(id, updateForm);
    };

    const activateModel = (id: number): Promise<void> => {
        return modelRepository.activateModel(id);
    };

    const deactivateModel = (id: number): Promise<void> => {
        return modelRepository.deactivateModel(id);
    };

    const getAllActiveModels = (): Promise<Model[]> => {
        return modelRepository.getAllActiveModels();
    };

    const getModelsDeliveryPricesStatuses = (modelIds: number[]): Promise<ModelDeliveryPriceStatus[]> => {
        return modelRepository.getModelsDeliveryPricesStatuses(modelIds);
    };

    const getModelDeliveryPriceStatus = (modelId: number): Promise<boolean> => {
        return getModelsDeliveryPricesStatuses([modelId]).then((status: ModelDeliveryPriceStatus[]) => {
            return status[0].exist_delivery;
        });
    };

    return {
        arModels,
        modelsPaginationMeta,
        isModelsLoading,
        getModels: getModels,
        getModel: getModel,
        updateModel: updateModel,
        createModel: createModel,
        activateModel: activateModel,
        deactivateModel: deactivateModel,
        getAllActiveModels: getAllActiveModels,
        getModelsDeliveryPricesStatuses: getModelsDeliveryPricesStatuses,
        getModelDeliveryPriceStatus: getModelDeliveryPriceStatus,
    };
}
